import katex from 'katex';

const latexIgnoredTags = new Set(['SCRIPT', 'STYLE', 'PRE', 'CODE', 'TEXTAREA', 'KBD', 'SAMP']);
const acuteAccentByVowel = {
  a: 'á',
  e: 'é',
  i: 'í',
  o: 'ó',
  u: 'ú',
  A: 'Á',
  E: 'É',
  I: 'Í',
  O: 'Ó',
  U: 'Ú'
};

const normalizeAssistantTextArtifacts = (text) => {
  const withAccents = String(text || '')
    .replace(/([AaEeIiOoUu])\s*[\u02ca\u00b4\u0301]\s*/g, (_, vowel) => acuteAccentByVowel[vowel] || vowel);

  return withAccents
    .replace(/,\s*susuma\b/gi, ', su suma')
    .replace(/,\s*dondecadaelemento\b/gi, ', donde cada elemento')
    .replace(/\)\s*ylaasociatividad\s*\(/gi, ') y la asociatividad (')
    .replace(/\bnexistelamatriznula\b/gi, 'n existe la matriz nula')
    .replace(/\bqueactúa?comoelelementoneutrodelasuma\b/gi, 'que actúa como el elemento neutro de la suma')
    .replace(/\byaquealsumarlaacualquiermatriz\b/gi, 'ya que al sumarla a cualquier matriz')
    .replace(/\belresultadoes\b/gi, 'el resultado es')
    .replace(/\$\s*([^$]*?\\times\s*[^$,]+?)\s*,\s*su suma\s+([^$]*?)\s*\$/gi, (_, dimensions, sumExpression) =>
      `$${dimensions.trim()}$, su suma $${sumExpression.trim()}$`)
    .replace(/\$\s*([^$]*?\\times\s*[^$,]+?)\s*,\s*donde cada elemento\s+([^$]*?)\s*\$/gi, (_, dimensions, elementExpression) =>
      `$${dimensions.trim()}$, donde cada elemento $${elementExpression.trim()}$`)
    .replace(/\(\$\s*(A\s*\+\s*B\s*=\s*B\s*\+\s*A)\s*\)\s*y la asociatividad\s*\(\s*(\(?A\s*\+\s*B\)?\s*\+\s*C\s*=\s*A\s*\+\s*\(?B\s*\+\s*C\)?)\s*\)/gi, (_, firstFormula, secondFormula) =>
      `($${firstFormula.trim()}$) y la asociatividad ($${secondFormula.trim()}$)`)
    .replace(/\s+([,.;])/g, '$1')
    .replace(/,([^\s\d])/g, ', $1')
    .replace(/([.;])(?=[^\s\d])/g, '$1 ')
    .replace(/\s+\$(?=\.|,|;|:|\s|$)/g, '');
};

const isEscapedDelimiter = (text, index) => {
  let slashCount = 0;
  for (let cursor = index - 1; cursor >= 0 && text[cursor] === '\\'; cursor -= 1) {
    slashCount += 1;
  }
  return slashCount % 2 === 1;
};

const findUnescapedToken = (text, token, fromIndex) => {
  let index = text.indexOf(token, fromIndex);
  while (index !== -1) {
    if (!isEscapedDelimiter(text, index)) {
      return index;
    }
    index = text.indexOf(token, index + token.length);
  }
  return -1;
};

const findSingleDollarClosing = (text, fromIndex) => {
  for (let index = fromIndex; index < text.length; index += 1) {
    if (text[index] === '$' && text[index + 1] !== '$' && !isEscapedDelimiter(text, index)) {
      return index;
    }
  }
  return -1;
};

const bareMathTokenPattern = [
  String.raw`\\[a-zA-Z]+(?:_\{[^}]+\}|\^\{[^}]+\}|_[A-Za-z0-9]+|\^[A-Za-z0-9]+)?`,
  String.raw`[A-Za-z](?:_\{[^}]+\}|\^\{[^}]+\}|_[A-Za-z0-9]+|\^[A-Za-z0-9]+)?`,
  String.raw`\d+(?:[.,]\d+)?`
].join('|');

const bareMathOperatorPattern = [
  String.raw`\\times`,
  String.raw`\\cdot`,
  String.raw`\\div`,
  String.raw`\\pm`,
  String.raw`\\leq`,
  String.raw`\\geq`,
  String.raw`[+\-=<>*/]`
].join('|');

const bareMathPatterns = [
  new RegExp(`(^|[^\\\\\\w])((?:${bareMathTokenPattern})(?:\\s*(?:${bareMathOperatorPattern})\\s*(?:${bareMathTokenPattern}))+)(?=$|[^\\\\\\w])`, 'g'),
  new RegExp(`(^|[^\\\\\\w])([A-Za-z](?:_\\{[^}]+\\}|\\^\\{[^}]+\\}|_[A-Za-z0-9]+|\\^[A-Za-z0-9]+))(?=$|[^\\\\\\w])`, 'g')
];

const findNextBareMath = (source, cursor) => {
  let bestMatch = null;

  bareMathPatterns.forEach((pattern) => {
    pattern.lastIndex = cursor;
    const match = pattern.exec(source);
    if (!match) return;

    const prefix = match[1] || '';
    const value = match[2] || '';
    const start = match.index + prefix.length;
    const end = start + value.length;
    if (!value.trim() || !isLikelyInlineMath(value)) {
      return;
    }

    if (
      !bestMatch ||
      start < bestMatch.start ||
      (start === bestMatch.start && end > bestMatch.end)
    ) {
      bestMatch = { start, end, value };
    }
  });

  return bestMatch;
};

const isLikelyInlineMath = (content) => {
  const trimmed = String(content || '').trim();
  if (!trimmed || trimmed.length > 300 || trimmed.includes('\n')) {
    return false;
  }
  if (/^[\d.,\s]+$/.test(trimmed)) {
    return false;
  }
  const withoutLatexCommands = trimmed
    .replace(/\\(?:begin|end)\{[^}]+\}/g, '')
    .replace(/\\[a-zA-Z]+/g, '')
    .replace(/\b(?:sin|cos|tan|log|ln|max|min|det|dim)\b/gi, '');
  if (/[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]{4,}/.test(withoutLatexCommands)) {
    return false;
  }
  return /\\[a-zA-Z]+|[A-Za-z]|[=<>^_{}+\-*/]/.test(trimmed);
};

const parseBareMathSegments = (text) => {
  const source = String(text || '');
  const segments = [];
  let cursor = 0;

  while (cursor < source.length) {
    const match = findNextBareMath(source, cursor);
    if (!match) {
      if (cursor < source.length) {
        segments.push({ type: 'text', value: source.slice(cursor) });
      }
      break;
    }

    if (match.start > cursor) {
      segments.push({ type: 'text', value: source.slice(cursor, match.start) });
    }

    segments.push({
      type: 'math',
      value: match.value.trim(),
      raw: match.value,
      displayMode: false
    });
    cursor = match.end;
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: source }];
};

const parseLatexSegments = (text) => {
  const source = String(text || '');
  const segments = [];
  let cursor = 0;
  let index = 0;

  const pushTextUntil = (endIndex) => {
    if (endIndex > cursor) {
      segments.push({ type: 'text', value: source.slice(cursor, endIndex) });
    }
  };

  const pushMath = (startIndex, endIndex, delimiterLength, displayMode, closingLength = delimiterLength) => {
    const value = source.slice(startIndex + delimiterLength, endIndex).trim();
    if (!value) {
      return false;
    }

    pushTextUntil(startIndex);
    segments.push({
      type: 'math',
      value,
      raw: source.slice(startIndex, endIndex + closingLength),
      displayMode
    });
    cursor = endIndex + closingLength;
    index = cursor;
    return true;
  };

  while (index < source.length) {
    if (source.startsWith('\\\\[', index) && !isEscapedDelimiter(source, index)) {
      const closingIndex = findUnescapedToken(source, '\\\\]', index + 3);
      if (closingIndex !== -1 && pushMath(index, closingIndex, 3, true, 3)) {
        continue;
      }
    }

    if (source.startsWith('\\\\(', index) && !isEscapedDelimiter(source, index)) {
      const closingIndex = findUnescapedToken(source, '\\\\)', index + 3);
      if (closingIndex !== -1 && pushMath(index, closingIndex, 3, false, 3)) {
        continue;
      }
    }

    if (source.startsWith('\\[', index) && !isEscapedDelimiter(source, index)) {
      const closingIndex = findUnescapedToken(source, '\\]', index + 2);
      if (closingIndex !== -1 && pushMath(index, closingIndex, 2, true)) {
        continue;
      }
    }

    if (source.startsWith('\\(', index) && !isEscapedDelimiter(source, index)) {
      const closingIndex = findUnescapedToken(source, '\\)', index + 2);
      if (closingIndex !== -1 && pushMath(index, closingIndex, 2, false)) {
        continue;
      }
    }

    if (source.startsWith('$$', index) && !isEscapedDelimiter(source, index)) {
      const closingIndex = findUnescapedToken(source, '$$', index + 2);
      if (closingIndex !== -1 && pushMath(index, closingIndex, 2, true)) {
        continue;
      }
    }

    if (source[index] === '$' && source[index + 1] !== '$' && !isEscapedDelimiter(source, index)) {
      const closingIndex = findSingleDollarClosing(source, index + 1);
      if (closingIndex !== -1) {
        const value = source.slice(index + 1, closingIndex);
        if (isLikelyInlineMath(value) && pushMath(index, closingIndex, 1, false)) {
          continue;
        }
      }
    }

    index += 1;
  }

  pushTextUntil(source.length);
  return segments;
};

const parseMathSegments = (text) =>
  parseLatexSegments(text).flatMap((segment) =>
    segment.type === 'text'
      ? parseBareMathSegments(segment.value)
      : [segment]
  );

const createLatexElement = (ownerDocument, segment) => {
  const element = ownerDocument.createElement('span');
  element.className = segment.displayMode
    ? 'ave-math ave-math-display'
    : 'ave-math ave-math-inline';

  try {
    element.innerHTML = katex.renderToString(segment.value, {
      displayMode: segment.displayMode,
      throwOnError: false,
      strict: 'ignore',
      trust: false,
      output: 'html'
    });
  } catch (error) {
    element.textContent = segment.raw;
  }

  return element;
};

export const renderLatexInHtml = (html) => {
  if (!html || typeof window === 'undefined' || !window.document?.implementation) {
    return html;
  }

  try {
    const ownerDocument = window.document.implementation.createHTMLDocument('');
    const template = ownerDocument.createElement('template');
    template.innerHTML = String(html);

    const visit = (node) => {
      if (!node) return;

      if (node.nodeType === window.Node.TEXT_NODE) {
        const originalText = node.textContent || '';
        const normalizedText = normalizeAssistantTextArtifacts(originalText);
        const segments = parseMathSegments(normalizedText);
        if (!segments.some((segment) => segment.type === 'math')) {
          if (normalizedText !== originalText) {
            node.textContent = normalizedText;
          }
          return;
        }

        const fragment = ownerDocument.createDocumentFragment();
        segments.forEach((segment) => {
          if (segment.type === 'math') {
            fragment.appendChild(createLatexElement(ownerDocument, segment));
          } else {
            fragment.appendChild(ownerDocument.createTextNode(segment.value));
          }
        });
        node.parentNode?.replaceChild(fragment, node);
        return;
      }

      if (node.nodeType === window.Node.ELEMENT_NODE && latexIgnoredTags.has(node.tagName)) {
        return;
      }

      Array.from(node.childNodes || []).forEach(visit);
    };

    visit(template.content);

    const container = ownerDocument.createElement('div');
    container.appendChild(template.content);
    return container.innerHTML;
  } catch (error) {
    return html;
  }
};

export const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
